import React, { ReactNode, useState, useContext } from "react";
import { NavLink, useLocation, useNavigate } from "react-router-dom";
import Logo from "./logo_color.png";
import IconExpand from "@/icons/IconExpand";
import IconCollapse from "@/icons/IconCollapse";
import useMenuItems from "@/hooks/useMenuItems";
import { MenuItem } from "@/types";
import MainLink from "./MainLink";
import ProfileButton from "./ProfileButton";
import { ProfileContext } from "@velaro/velaro-component-lib";
import { StatusContext } from "@velaro/velaro-component-lib";
import ExpandableMenuLink from "./ExpandableMenuLink";
const LogoWhite = "/img/Logo_White.svg";
const messagingClientUrl = import.meta.env.VITE_MESSAGING_CLIENT_URL as string;
import { LinkExternal01 } from "@velaro/velaro-component-lib";
import DesktopProfileMenu from "./DesktopProfileMenu";
import Tooltip from "@/components/Tooltip";
import CopilotAssistant from "@/components/CopilotAssistant";
import useVersion from "@/hooks/useVersion";

interface Props {
  children: ReactNode;
}

const styles = {
  subLink:
    "ml-5 p-2 hover:bg-cornflower-blue-400 block rounded-lg transition duration-200 delay-100 hover:delay-100",
  subLinkActive:
    "ml-5 p-2 rounded-lg bg-cornflower-blue-700 block transition duration-200 delay-100 hover:delay-100"
};

function SubLink({
  to,
  label,
  onVisit
}: {
  to: string;
  label: string;
  onVisit(): void;
}) {
  if (to.startsWith("http")) {
    return (
      <a
        href={to}
        target="_blank"
        rel="noreferrer"
        className={styles.subLink}
        onClick={onVisit}
      >
        <span className="w-full text-left text-sm">{label}</span>
      </a>
    );
  }
  return (
    <NavLink
      to={to}
      end
      className={({ isActive }) =>
        isActive ? styles.subLinkActive : styles.subLink
      }
      onClick={onVisit}
    >
      <span className="w-full text-left text-sm">{label}</span>
    </NavLink>
  );
}
export default function MainLayout(props: Props) {
  const [leftNavExpanded, setLeftNavExpanded] = useState(true);
  const [expandedMenus, setExpandedMenus] = useState<string[]>([]);
  const [toggleSidebar, setToggleSidebar] = useState(true);
  const [showProfileMenu, setShowProfileMenu] = useState(false);
  const [showCopilot, setShowCopilot] = useState(false);

  const location = useLocation();
  const navigate = useNavigate();
  const menuItems = useMenuItems();

  const { profile } = useContext(ProfileContext);
  const { status } = useContext(StatusContext);
  const { showReloadModal, resetModal } = useVersion();

  const availabilityColor =
    status === "Available"
      ? "bg-green-500"
      : status === "Unavailable"
      ? "bg-sweet-orange-500"
      : "bg-slate-500";

  const sidebarClass = toggleSidebar ? "w-64" : "w-14";
  const toggleSidebarIcon = () => setToggleSidebar((prev) => !prev);

  const expandMenu = (menuId: string) => {
    setExpandedMenus((prev) => {
      const index = prev.indexOf(menuId);
      return index > -1
        ? prev.filter((id) => id !== menuId)
        : [...prev, menuId];
    });
  };

  const renderMenu = (item: MenuItem, index: number) => {
    if (!item.enabled) return null;

    const onVisit = () => {
      if (!leftNavExpanded) setLeftNavExpanded(false);
    };

    if (!item.sublinks) {
      return (
        <MainLink
          key={index}
          label={item.label}
          to={item.to}
          icon={item.icon}
          onVisit={onVisit}
          showTooltip={toggleSidebar}
          toggleSidebar={toggleSidebar}
        />
      );
    }

    return (
      <ExpandableMenuLink
        key={index}
        onExpand={() => expandMenu(item.label)}
        label={toggleSidebar ? item.label : ""}
        icon={item.icon}
        expanded={expandedMenus.includes(item.label)}
        toggleSidebar={toggleSidebar}
      >
        {leftNavExpanded &&
          item.sublinks.map((sublink) =>
            sublink.enabled ? (
              !toggleSidebar ? (
                <SubLink
                  key={sublink.to}
                  to={sublink.to}
                  label={sublink.label}
                  onVisit={onVisit}
                />
              ) : (
                <div key={sublink.to} className="border-l ml-5">
                  <SubLink
                    to={sublink.to}
                    label={sublink.label}
                    onVisit={onVisit}
                  />
                </div>
              )
            ) : null
          )}
      </ExpandableMenuLink>
    );
  };

  return (
    <div className="flex flex-col h-screen overflow-hidden">
      <div className="main-layout flex h-screen overflow-hidden">
        <div
          className={`flex flex-col flex-none px-2 bg-cornflower-blue-500 text-white z-50 ${
            toggleSidebar ? "w-64" : "w-[52px]"
          } ${leftNavExpanded ? "menu_active" : ""}`}
        >
          <div className="flex items-center justify-between pt-6 pb-5">
            <img
              src={LogoWhite}
              alt="Logo"
              className="w-[44px] h-[38px] cursor-pointer"
              onClick={() => navigate("/")}
            />
            <Tooltip
              text={toggleSidebar ? "Collapse" : "Expand"}
              direction="right"
              minWidth="70px"
              offset={toggleSidebar ? "15px" : "25px"}
            >
              <button
                onClick={toggleSidebarIcon}
                className={`hover:bg-cornflower-blue-400 p-2 rounded ${
                  toggleSidebar
                    ? "text-white"
                    : "fill-black relative left-6 hover:bg-slate-gray-200"
                }`}
              >
                {toggleSidebar ? <IconCollapse /> : <IconExpand />}
              </button>
            </Tooltip>
          </div>

          <div
            className="overflow-y-auto flex-1 overflow-x-hidden"
            style={{
              maxHeight: "calc(100vh - 4rem)",
              scrollbarColor: "#d1e4ff #3794ff",
              scrollbarWidth: "thin"
            }}
          >
            {menuItems.map(renderMenu)}
          </div>

          <div className="border-t border-cornflower-blue-400 my-2">
            <div className="py-2">
              <MainLink
                to={messagingClientUrl}
                label="Agent Workspace"
                icon={<LinkExternal01 />}
                onVisit={() => {}}
                showTooltip={toggleSidebar}
                toggleSidebar={toggleSidebar}
                target="_blank"
              />
            </div>
            <div className="border-t border-cornflower-blue-400"></div>
            <ProfileButton
              picture={profile.picture}
              availabilityColor={availabilityColor}
              onToggle={() => setShowProfileMenu((prev) => !prev)}
              label={
                profile.firstName && profile.lastName
                  ? `${profile.firstName} ${profile.lastName}`
                  : profile.displayName
              }
              toggleSidebar={toggleSidebar}
            />
            {showProfileMenu && (
              <DesktopProfileMenu
                onClose={() => setShowProfileMenu(false)}
                profile={profile}
              />
            )}
            <div className="border-t border-cornflower-blue-400"></div>
          </div>
        </div>

        <main
          className={`flex-auto min-w-0 bg-slate-gray-50 shrink overflow-auto h-full ${
            toggleSidebar ? "p-6" : "py-6 pr-6 pl-16"
          }`}
        >
          {/* Update notification — shown when the server version changes */}
          {showReloadModal && (
            <div className="flex items-center justify-between mb-4 px-4 py-3 bg-cornflower-blue-600 text-white rounded-lg shadow">
              <span className="text-sm font-medium">
                A new version of the app is available.
              </span>
              <div className="flex items-center gap-3 ml-4">
                <button
                  onClick={() => window.location.reload()}
                  className="text-sm font-semibold bg-white text-cornflower-blue-600 px-3 py-1 rounded hover:bg-cornflower-blue-50 transition-colors"
                >
                  Reload now
                </button>
                <button
                  onClick={resetModal}
                  className="text-sm text-cornflower-blue-100 hover:text-white transition-colors"
                  aria-label="Dismiss update notification"
                >
                  ✕
                </button>
              </div>
            </div>
          )}
          {props.children}
        </main>
      </div>

      {/* Velaro Copilot — global FAB, always available on every page */}
      {!showCopilot && (
        <button
          type="button"
          onClick={() => setShowCopilot(true)}
          className="fixed bottom-6 right-6 z-[9999] flex items-center gap-2 bg-cornflower-blue-600 hover:bg-cornflower-blue-700 text-white rounded-full px-4 py-3 shadow-lg transition-colors text-sm font-medium"
          aria-label="Open Velaro Copilot"
        >
          <svg width="20" height="17" viewBox="0 0 40 34" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M19.3703 13.4165C20.0828 13.3996 20.7841 13.5954 21.3848 13.979C21.9854 14.3625 22.4581 14.9164 22.7425 15.5699C23.0269 16.2233 23.1101 16.9467 22.9814 17.6477C22.8528 18.3487 22.5181 18.9954 22.0202 19.5052C21.5223 20.0151 20.8837 20.365 20.186 20.5102C19.4883 20.6555 18.7631 20.5895 18.1031 20.3207C17.4431 20.0519 16.8781 19.5924 16.4804 19.001C16.0827 18.4097 15.8703 17.7132 15.8703 17.0005C15.8599 16.0613 16.2227 15.1564 16.8789 14.4844C17.5351 13.8124 18.4312 13.4284 19.3703 13.4165Z" fill="white"/>
            <path d="M16.5022 33.5695C-3.49176 19.6415 5.20024 10.5985 18.7612 10.0495C24.8982 9.8055 25.0852 -0.350501 18.5192 0.480499C12.0452 1.3095 -1.93776 6.7725 0.636237 16.7095C2.59324 24.2635 11.8612 31.9195 16.5022 33.5695Z" fill="white"/>
            <path d="M22.2475 0.431499C42.2415 14.3585 33.5475 23.4025 19.9885 23.9505C13.8515 24.1955 13.6645 34.3555 20.2295 33.5195C26.7055 32.6915 40.6885 27.2315 38.1145 17.2915C36.1575 9.7375 26.8895 2.0815 22.2475 0.431499Z" fill="white"/>
          </svg>
          Ask Copilot
        </button>
      )}
      {showCopilot && (
        <CopilotAssistant
          context={`Current page: ${location.pathname}. Use [→ Label](/path) format when directing the user to any page.`}
          onClose={() => setShowCopilot(false)}
        />
      )}
    </div>
  );
}
